import { teamAPI } from 'api/teamAPI';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import produce from 'immer';

export const fetchTopRanks = createAsyncThunk(
    'team/fetchTopRanks',
    async (params) => await teamAPI.fetchTopRanks(params)
);

export const fetchPeerComparison = createAsyncThunk(
    'team/fetchPeerComparison',
    async (params) => await teamAPI.fetchPeerComparison(params)
);

export const fetchTrendAnalysis = createAsyncThunk(
    'team/fetchTrendAnalysis',
    async (params) => await teamAPI.fetchTrendAnalysis(params)
);

export const fetchTopAndEmergingSpikes = createAsyncThunk(
    'team/fetchTopAndEmergingSpikes',
    async (params) => await teamAPI.fetchTopAndEmergingSpikes(params)
);

export const fetchScenarioStats = createAsyncThunk(
    'team/fetchScenarioStats',
    async (params) => await teamAPI.fetchScenarioStats(params)
);

const teamSlice = createSlice({
    name: 'team',
    initialState: {
        topFiveRmsData: null,
        peerComparisonChartData: null,
        trendAnalysisChartData: null,

        minorThreshold: null,
        majorThreshold: null,
        topFiveEmergingMajorRm: null,
        topFiveEmergingModerateRm: null,
        topFiveRmsSpike: null,

        statsReporting: null,

        topFiveRmsDataFetched: false,
        peerComparisonChartDataFetched: false,
        trendAnalysisChartDataFetched: false,
        topAndEmergingSpikesFetched: false,
        statsReportingFetched: false
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchTopRanks.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { topFiveRm } = payload;
                draft.topFiveRmsData = topFiveRm;
                draft.topFiveRmsDataFetched = true;

                if (topFiveRm.length > 0) {
                    const { moderateThreshold, majorThreshold } = topFiveRm[0];
                    draft.minorThreshold = moderateThreshold;
                    draft.majorThreshold = majorThreshold;
                }
            })
        );
        builder.addCase(fetchPeerComparison.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { peerComparisonChartData } = payload;
                draft.peerComparisonChartData = peerComparisonChartData;
                draft.peerComparisonChartDataFetched = true;
            })
        );
        builder.addCase(fetchTrendAnalysis.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { trendAnalysisChartData } = payload;
                draft.trendAnalysisChartData = trendAnalysisChartData;
                draft.trendAnalysisChartDataFetched = true;
            })
        );
        builder.addCase(
            fetchTopAndEmergingSpikes.fulfilled,
            (state, { payload }) =>
                produce(state, (draft) => {
                    const {
                        topFiveEmergingMajorRm,
                        topFiveEmergingModerateRm,
                        topFiveRmSpike
                    } = payload;
                    const emergingRmsToRetrieveThreshold = () => {
                        if (topFiveRmSpike.length > 0) return topFiveRmSpike;
                        if (topFiveEmergingModerateRm.length > 0)
                            return topFiveEmergingModerateRm;
                        if (topFiveEmergingMajorRm.length > 0)
                            return topFiveEmergingMajorRm;
                        return [];
                    };
                    if (emergingRmsToRetrieveThreshold().length > 0) {
                        const { moderateThreshold, majorThreshold } =
                            emergingRmsToRetrieveThreshold()[0];
                        draft.minorThreshold = moderateThreshold;
                        draft.majorThreshold = majorThreshold;
                    }
                    draft.topFiveEmergingMajorRm = topFiveEmergingMajorRm;
                    draft.topFiveEmergingModerateRm = topFiveEmergingModerateRm;
                    draft.topFiveRmsSpike = topFiveRmSpike;
                    draft.topAndEmergingSpikesFetched = true;
                })
        );
        builder.addCase(fetchScenarioStats.fulfilled, (state, { payload }) =>
            produce(state, (draft) => {
                const { statsReporting } = payload;
                draft.statsReporting = statsReporting;
                draft.statsReportingFetched = true;
            })
        );
    }
});

const { reducer: teamReducer } = teamSlice;
export default teamReducer;
